import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  Container,
  CircularProgress,
  Button,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  IconButton,
} from "@mui/material";
import { InputAdornment, TextField, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useTranslation } from "react-i18next";
import { useViewport, pictoWebVr } from "../../../helpers";
import { QRCode } from "react-qrcode-logo";
import clsx from "clsx";
import ViewInAR from "../../../assets/viewInAR.svg";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useSnackbar } from "notistack";
import ShareIcon from "@mui/icons-material/Share";
import LinkIcon from "@mui/icons-material/Link";
import FanArtZone from "../../../assets/faz.png";
import { myExpariencesService } from "../../../services";
import ResponsiveDialogManager from "../../../components/dialog/ResponsiveDialogManager";
import { objectSizeCameraPos } from "../../../helpers/model-viewer-camera-position";
import VisibilityAndOrder from "./VisibilityAndOrder";

const PREFIX = "WebVR";

const classes = {
  root: `${PREFIX}-root`,
  vrContainer: `${PREFIX}-vrContainer`,
  hiddingVrForLoading: `${PREFIX}-hiddingVrForLoading`,
  optionsContainer: `${PREFIX}-optionsContainer`,
  selectSizeContainer: `${PREFIX}-selectSizeContainer`,
  qrPosition: `${PREFIX}-qrPosition`,
  downloadQRButton: `${PREFIX}-downloadQRButton`,
  webVrButton: `${PREFIX}-webVrButton`,
  activateARText: `${PREFIX}-activateARText`,
  centerText: `${PREFIX}-centerText`,
  shareIconButton: `${PREFIX}-shareIconButton`,
  buttonsQrCode: `${PREFIX}-buttonsQrCode`,
  copyQrUrlButton: `${PREFIX}-copyQrUrlButton`,
  fetchingContainer: `${PREFIX}-fetchingContainer`,
  webVrPoster: `${PREFIX}-webVrPoster`,
  selectContainer: `${PREFIX}-selectContainer`,
  imageExparienceDropdown: `${PREFIX}-imageExparienceDropdown`,
  exparience3DcomponentDropdown: `${PREFIX}-exparience3DcomponentDropdown`,
  selectText: `${PREFIX}-selectText`,
  uploadHdrContainer: `${PREFIX}-uploadHdrContainer`,
  changeHdrSelect: `${PREFIX}-changeHdrSelect`,
  errorEmailStyle: `${PREFIX}-errorEmailStyle`,
  updateVisiblityButton: `${PREFIX}-updateVisiblityButton`,
  marginTop: `${PREFIX}-marginTop`,
  noFramesVisible: `${PREFIX}-noFramesVisible`,
  selectMenuStyle: `${PREFIX}-selectMenuStyle`,
  webArAction: `${PREFIX}-webArAction`,
  webArActionStyle: `${PREFIX}-webArActionStyle`,
};

const StyledDiv = styled("div")(({ theme }) => ({
  [`& .${classes.root}`]: {
    overflow: "hidden",
    display: "flex",
    [theme.breakpoints.between("xs", "md")]: {
      flexDirection: "column",
    },
  },

  [`& .${classes.vrContainer}`]: {
    position: "relative",
    width: "50%",
    zIndex: "10",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
    },
  },

  [`& .${classes.hiddingVrForLoading}`]: {
    position: "absolute",
    width: "100%",
    height: "101%",
    zIndex: 11,
    backgroundColor: "#303030",
    textAlign: "center",
    paddingTop: "40%",
    paddingBottom: "10%",
  },

  [`& .${classes.optionsContainer}`]: {
    width: "50%",
    margin: "0 auto",
    padding: " 0 2rem",
    [theme.breakpoints.between("xs", "md")]: {
      width: "100%",
      padding: "inherit",
      margin: "1rem 0",
    },
  },

  [`& .${classes.selectSizeContainer}`]: {
    margin: "1rem 0 1rem 0",
    [theme.breakpoints.between("xs", "md")]: {
      margin: "0 0 1rem 0",
    },
    width: "100%",
  },

  [`& .${classes.qrPosition}`]: {
    display: "flex",
    justifyContent: "center",
    marginTop: "2rem",
  },

  [`& .${classes.downloadQRButton}`]: {
    margin: "1rem 0",
  },

  [`& .${classes.webVrButton}`]: {
    backgroundColor: theme.palette.primary.main,
    color: "#434343",
    borderRadius: "4px",
    border: "none",
    position: "absolute",
    top: "1.5rem",
    right: "16px",
    zIndex: "999",
    display: "flex",
  },

  [`& .${classes.activateARText}`]: {
    transform: "translateY(5px)",
    marginLeft: "0.5rem",
  },

  [`& .${classes.centerText}`]: {
    top: "2rem",
    textAlign: "center",
  },

  [`& .${classes.shareIconButton}`]: {
    color: "#343434",
    backgroundColor: theme.palette.primary.main,
    width: "40px",
    height: "40px",
    margin: "1rem 0",
    "&:hover": {
      color: theme.palette.primary.main,
      backgroundColor: "#343434",
    },
  },

  [`& .${classes.buttonsQrCode}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.copyQrUrlButton}`]: {
    display: "flex",
    justifyContent: "center",
  },

  [`& .${classes.fetchingContainer}`]: {
    height: "60px",
    width: "250px",
    margin: "2rem auto",
    textAlign: "center",
  },

  [`& .${classes.webVrPoster}`]: {
    position: "absolute",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    backgroundImage: `url(${FanArtZone})`,
    backgroundSize: "250px 250px",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center",
  },

  [`& .${classes.selectContainer}`]: {
    display: "flex",
    flexDirection: "row",
    "& > img": {
      textAlign: "left",
      marginRight: "3rem",
      objectFit: "contain",
    },
    "& > div": {
      textAlign: "left",
      marginRight: "3rem",
    },
  },

  [`& .${classes.imageExparienceDropdown}`]: {
    width: "60px",
    height: "60px",
    objectFit: "contain",
  },

  [`& .${classes.exparience3DcomponentDropdown}`]: {
    height: "60px",
    display: "flex",
    alignItems: "center",
  },

  [`& .${classes.selectText}`]: {
    marginRight: "3rem",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },

  [`& .${classes.uploadHdrContainer}`]: {
    display: "flex",
    justifyContent: "space-around",
  },

  [`& .${classes.changeHdrSelect}`]: {
    margin: "1rem 0.5rem 1rem 0",
    width: "100%",
    height: "50px",
  },

  [`& .${classes.errorEmailStyle}`]: {
    color: "#f44336",
  },

  [`& .${classes.updateVisiblityButton}`]: {
    width: "70%",
    marginBottom: "1rem",
    display: "flex",
    margin: "0 auto",
  },

  [`& .${classes.marginTop}`]: {
    marginTop: "1rem",
  },

  [`& .${classes.noFramesVisible}`]: {
    textAlign: "center",
    marginBottom: "1rem",
  },

  [`& .${classes.selectMenuStyle}`]: {
    backgroundColor: "#424242",
  },

  [`& .${classes.webArAction}`]: {
    margin: "1rem 0",
  },

  [`& .${classes.webArActionStyle}`]: {
    width: "100%",
  },
}));

const WebVR = (props) => {
  const { t } = useTranslation();
  const { width } = useViewport();
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFrame, setSelectedFrame] = useState(null);
  const [activatedSwap, setActivatedSwap] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [picto, setPicto] = useState(null);
  const [noPicture, setNoPicture] = useState(false);
  const canvasRef = useRef(null);
  // const [loading, setLoading] = useState({ dimensions: false, picture: false });
  const [loading, setLoading] = useState({ picture: false });
  const [copied, setCopied] = useState(false);
  const [dimensions, setDimensions] = useState({});
  const [allHdr, setAllHdr] = useState(null);
  const [hdrImage, setHdrImage] = useState(null);
  const [updateVisibility, setUpdateVisibility] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);
  const [webArActionText, setWebArActionText] = useState("");
  const [webArActionsDisable, setWebArActionsDisable] = useState(true);
  const [webArActionsValue, setWebArActionsValue] = useState("");
  const [webActionLoader, setWebActionLoader] = useState(false);

  const modelViewerRef = useRef(null);

  const generateUrl = () => {
    setQrUrl(selectedFrame.shortLink);
  };

  useEffect(() => {
    if (props.visibleFrames.length > 0 && selectedFrame) {
      if (!selectedFrame.imageUrl && selectedFrame.link) {
        const tmp = document.getElementById("model-viewer");
        if (tmp) tmp.scale = `1 1 1`;
        setIsLandscape(false);
        setDimensions({});
        generateUrl();
      }
      if (selectedFrame.imageUrl) {
        // setLoading({ dimensions: true, picture: true });
        setLoading({ picture: true });
        let w;
        let h;
        const img = new Image();

        const src = selectedFrame.imageUrl;
        const options = {
          headers: {
            // Pragma: "no-cache",
            // "Cache-Control": "no-store",
          },
        };

        fetch(src, options)
          .then((res) => res.blob())
          .then((blob) => {
            img.src = URL.createObjectURL(blob);
            img.onload = function () {
              w = this.width;
              h = this.height;
              // if (
              //   dimensions.x === w &&
              //   dimensions.y === h &&
              //   dimensions.z === 1
              // ) {
              //   console.log("inside");
              // }
              setDimensions({ x: w, y: h, z: 1 });
            };
          });
        generateUrl();
        let tmp;
        let inter = setInterval(() => {
          tmp = document.getElementById("model-viewer");
          if (tmp !== null && tmp?.model?.materials?.length > 0) {
            if (!activatedSwap) {
              setActivatedSwap(true);
              tmp.addEventListener("ar-status", (event) => {
                if (event.detail.status === "failed") {
                  // if device is not compatible AR
                  const error = document.getElementById("error");
                  error.classList.remove("hide");
                  error.addEventListener("transitionend", (event) => {
                    error.classList.add("hide");
                  });
                }
              });

              //panning (relocate camera by touching/clicking)
              const tapDistance = 2;
              let panning = false;
              let panX, panY;
              let startX, startY;
              let lastX, lastY;
              let metersPerPixel;

              const startPan = () => {
                const orbit = tmp.getCameraOrbit();
                const { theta, phi, radius } = orbit;
                const psi = theta - tmp.turntableRotation;
                metersPerPixel =
                  (0.75 * radius) / tmp.getBoundingClientRect().height;
                panX = [-Math.cos(psi), 0, Math.sin(psi)];
                panY = [
                  -Math.cos(phi) * Math.sin(psi),
                  Math.sin(phi),
                  -Math.cos(phi) * Math.cos(psi),
                ];
                tmp.interactionPrompt = "none";
              };

              const movePan = (thisX, thisY) => {
                const dx = (thisX - lastX) * metersPerPixel;
                const dy = (thisY - lastY) * metersPerPixel;
                lastX = thisX;
                lastY = thisY;

                const target = tmp.getCameraTarget();
                target.x += dx * panX[0] + dy * panY[0];
                target.y += dx * panX[1] + dy * panY[1];
                target.z += dx * panX[2] + dy * panY[2];
                tmp.cameraTarget = `${target.x}m ${target.y}m ${target.z}m`;

                // This pauses turntable rotation
                tmp.dispatchEvent(
                  new CustomEvent("camera-change", {
                    detail: { source: "user-interaction" },
                  })
                );
              };

              const recenter = (pointer) => {
                panning = false;
                if (
                  Math.abs(pointer.clientX - startX) > tapDistance ||
                  Math.abs(pointer.clientY - startY) > tapDistance
                )
                  return;
                const hit = tmp.positionAndNormalFromPoint(
                  pointer.clientX,
                  pointer.clientY
                );
                tmp.cameraTarget =
                  hit == null ? "auto auto auto" : hit.position.toString();
              };

              tmp.addEventListener(
                "mousedown",
                (event) => {
                  startX = event.clientX;
                  startY = event.clientY;
                  panning =
                    event.button === 2 ||
                    event.ctrlKey ||
                    event.metaKey ||
                    event.shiftKey;
                  if (!panning) return;

                  lastX = startX;
                  lastY = startY;
                  startPan();
                  event.stopPropagation();
                },
                true
              );

              tmp.addEventListener(
                "touchstart",
                (event) => {
                  const { targetTouches, touches } = event;
                  startX = targetTouches[0].clientX;
                  startY = targetTouches[0].clientY;
                  panning =
                    targetTouches.length === 2 &&
                    targetTouches.length === touches.length;
                  if (!panning) return;

                  lastX =
                    0.5 * (targetTouches[0].clientX + targetTouches[1].clientX);
                  lastY =
                    0.5 * (targetTouches[0].clientY + targetTouches[1].clientY);
                  startPan();
                },
                true
              );

              window.addEventListener(
                "mousemove",
                (event) => {
                  if (!panning) return;

                  movePan(event.clientX, event.clientY);
                  event.stopPropagation();
                },
                true
              );

              tmp.addEventListener(
                "touchmove",
                (event) => {
                  if (!panning || event.targetTouches.length !== 2) return;

                  const { targetTouches } = event;
                  const thisX =
                    0.5 * (targetTouches[0].clientX + targetTouches[1].clientX);
                  const thisY =
                    0.5 * (targetTouches[0].clientY + targetTouches[1].clientY);
                  movePan(thisX, thisY);
                },
                true
              );

              window.addEventListener(
                "mouseup",
                (event) => {
                  recenter(event);
                },
                true
              );
            }
            let painting = tmp.model.materials.filter((material) =>
              material.name.includes("content")
            )[0];
            painting?.setAlphaMode("BLEND");
            let createAndApplyTexture = async (channel) => {
              const texture = await tmp.createTexture(selectedFrame.imageUrl);
              await painting.pbrMetallicRoughness[channel].setTexture(texture);
              setLoading({ ...loading, picture: false });
            };
            if (selectedFrame.imageUrl) {
              createAndApplyTexture("baseColorTexture");
            }
            clearInterval(inter);
          }
        }, 1000);
      } else {
        if (!selectedFrame.imageUrl && !selectedFrame.link) {
          setNoPicture(true);
        }
      }
    }
    setWebArActionsValue(
      !selectedFrame?.webArAction ||
        selectedFrame?.webArAction === props.exparience.arLink
        ? props.exparience.arLink
        : selectedFrame.webArAction
    );
  }, [selectedFrame]);

  useEffect(() => {
    setPicto(pictoWebVr);
    setSelectedFrame(
      props.visibleFrames.length > 0 ? props.visibleFrames[0] : null
    );
    myExpariencesService
      .getAllHdr()
      .then((res) => {
        setAllHdr(res);
        if (props.exparience?.hdr) {
          setHdrImage(props.exparience.hdr);
        } else {
          const hdrLink =
            res.length > 0
              ? res.find((item) => item.url.includes("360"))?.url
              : "https://ar-poc.s3.fr-par.scw.cloud/test/360.hdr";
          props.setExparience({
            ...props.exparience,
            hdr: hdrLink,
          });
          setHdrImage(hdrLink);
        }
      })
      .catch((err) => {
        console.log(err);
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else enqueueSnackbar(t("Crm.toasts.getFail"), { variant: "error" });
      });
  }, []);

  useEffect(() => {
    if (props.visibleFrames.length > 0) {
      setSelectedFrame(props.visibleFrames[0]);
    } else if (props.visibleFrames.length <= 0) {
      setSelectedFrame(null);
    }
  }, [props.visibleFrames]);

  React.useEffect(() => {
    if (props.visibleFrames.length > 0 && Object.keys(dimensions).length > 0) {
      generateUrl();
      let x = 1,
        y = 1,
        z = 1;
      if (selectedFrame.imageUrl) {
        if (dimensions.x === dimensions.y) {
          setIsLandscape(false);
          x = 1.34;
          y = 1;
          z = 1;
        } else if (dimensions.x > dimensions.y) {
          setIsLandscape(true);
          const ratio = dimensions.x / dimensions.y;
          x = ratio;
          y = 1.34;
          z = 1;
        } else {
          setIsLandscape(false);
          const ratio = dimensions.y / dimensions.x;
          x = 1;
          y = ratio / 1.34;
          z = 1;
        }
      }
      const tmp = document.getElementById("model-viewer");
      // to check if it solve the problem
      if (tmp) tmp.scale = `${x} ${y} ${z}`;
    }
  }, [dimensions]);

  const downloadQR = () => {
    const fileName = selectedFrame.hasOwnProperty("imageUrl")
      ? selectedFrame.imageUrl
          .split("/")
          [selectedFrame.imageUrl.split("/").length - 1].split(".")[0]
          .replaceAll(/\s/g, "")
      : selectedFrame.link
          .split("/")
          [selectedFrame.link.split("/").length - 1].split(".")[0]
          .replaceAll(/\s/g, "");
    let canvas = canvasRef.current.querySelector("canvas");
    let image = canvas.toDataURL("image/jpg");
    let anchor = document.createElement("a");
    anchor.href = image;
    anchor.download = `QRCode-${fileName}.png`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  };

  const shareMedia = () => {
    navigator.share({
      title: document.title,
      text: `${t("myDesign.webVR.shareExparienceStart")}${
        props.exparience.displayName || props.exparience.name
      }${t("myDesign.webVR.shareExparienceEnd")}`,
      url: qrUrl,
    });
  };

  // const handleUploadHdr = (event) => {
  //   setLoading({ ...loading, picture: true });
  //   myExpariencesService
  //     .updateHdrImage(event.target.files, props.exparience._id)
  //     .then((res) => {
  //       setHdrImage(res.hdr);
  //       setLoading({ ...loading, picture: false });
  //       props.setExparience({ ...props.exparience, hdr: res.hdr });
  //       enqueueSnackbar(t("toasts.hdrImageUpdated"), { variant: "success" });
  //     })
  //     .catch((err) => {
  //       setLoading({ ...loading, picture: false });
  //       enqueueSnackbar(
  //         err === "image-error"
  //           ? t("toasts.hdrImageUpdateError")
  //           : t("toasts.unknownError"),
  //         { variant: "error" }
  //       );
  //     });
  // };

  useEffect(() => {
    if (props.exparience) {
      setWebArActionText(
        !props.exparience.webArAction ||
          props.exparience.webArAction?.length <= 0
          ? props.exparience.arLink
          : props.exparience.webArAction
      );
    }
  }, [props.exparience]);

  // useEffect(() => {
  //   let inter = setInterval(() => {
  //     const modelViewer = document.querySelector("#model-viewer");
  //     if (inter && modelViewer) clearInterval(inter);
  //     console.log("MV", modelViewer);
  //     modelViewer?.addEventListener("ar-status", (event) => {
  //       console.log("event =>", event);
  //       if (event.detail.status === "failed") {
  //         const error = document.querySelector("#error");
  //         error.classList.remove("hide");
  //         error.addEventListener("transitionend", (event) => {
  //           error.classList.add("hide");
  //         });
  //       }
  //     });
  //   }, 500);
  // }, [modelViewerRef]);

  if (props.frames.length <= 0 || noPicture) {
    return (
      <StyledDiv>
        <div
          style={{
            maxWidth: width > 1024 ? "80%" : "inherit",
            margin: "0 auto",
            marginBottom: width < 1024 ? "5rem" : "1rem",
          }}
        >
          <Typography className={classes.centerText}>
            {t("myExpariences.noFramesToDisplay")}
          </Typography>
        </div>
      </StyledDiv>
    );
  } else if (
    !updateVisibility &&
    (!allHdr ||
      !props.exparience ||
      (props.frames.length > 0 &&
        selectedFrame?.type !== "custom" &&
        Object.keys(dimensions).length <= 0 &&
        props.visibleFrames.length > 0) ||
      (!hdrImage && props.visibleFrames.length > 0))
  ) {
    return (
      <StyledDiv>
        <div className={classes.fetchingContainer}>
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </div>
      </StyledDiv>
    );
  } else {
    // console.log(
    //   "visibleFrames =>",
    //   props.visibleFrames,
    //   props.exparience.objects
    // );
    return (
      <Container>
        <StyledDiv>
          <ResponsiveDialogManager
            title={t("myDesign.webVR.updateVisibility")}
            fullScreen
            open={updateVisibility}
            onClose={() => {
              setUpdateVisibility(false);
            }}
            renderer={
              <VisibilityAndOrder
                frames={props.frames}
                setFrames={props.setFrames}
                visibleFrames={props.visibleFrames}
                setVisibleFrames={props.setVisibleFrames}
                exparience={props.exparience}
                setExparience={props.setExparience}
                isAdmin={props.isAdmin}
              />
            }
          />
          <div
            style={{
              maxWidth: width > 1024 ? "80%" : "inherit",
              margin: "0 auto",
              marginBottom: width < 1024 ? "5rem" : "1rem",
            }}
          >
            <div className={classes.root}>
              {props.visibleFrames.length > 0 && selectedFrame && (
                <div className={classes.vrContainer}>
                  {loading.picture && (
                    <div className={classes.hiddingVrForLoading}>
                      <CircularProgress size={50} />
                      <Typography>{t("loaders.updatingVRModel")}</Typography>
                    </div>
                  )}
                  <model-viewer
                    src={
                      selectedFrame.type === "custom" && !selectedFrame.imageUrl
                        ? selectedFrame.link
                        : `https://ar-prod.s3.fr-par.scw.cloud/0_FRAMES_AR/${
                            selectedFrame.molding
                          }${isLandscape ? "-landscape" : ""}${`${
                            selectedFrame.molding === "none"
                              ? ""
                              : `-${
                                  selectedFrame.size === "custom"
                                    ? "large"
                                    : !selectedFrame.size
                                    ? "medium"
                                    : selectedFrame.size
                                }`
                          }`}.glb`
                    }
                    ios-src={selectedFrame.usdz || null}
                    skybox-image={hdrImage}
                    alt="3D model in VR"
                    ar
                    autoplay
                    shadow-intensity="1"
                    ar-modes="webxr scene-viewer quick-look"
                    camera-controls
                    touch-action="pan-y"
                    // We place all object on the wall to move them verticaly
                    ar-placement={
                      selectedFrame.type === "custom" ? "floor" : "wall"
                    }
                    id="model-viewer"
                    min-field-of-view="70deg"
                    max-field-of-view="85deg"
                    min-camera-orbit={
                      selectedFrame.type === "custom"
                        ? ""
                        : "-Infinity 22.5deg 0.5m"
                    }
                    max-camera-orbit={
                      selectedFrame.type === "custom"
                        ? ""
                        : "Infinity 157.5deg 10m"
                    }
                    camera-orbit={
                      selectedFrame.type === "custom"
                        ? ""
                        : `0deg 90deg ${
                            objectSizeCameraPos[selectedFrame.size][
                              isLandscape ? "landscape" : "portrait"
                            ]
                          }`
                    } // initial angle and position of camera
                    style={{
                      width: "99%",
                      height: width > 960 ? "600px" : "400px",
                    }}
                    ref={modelViewerRef}
                    // onTouchStart={handleTouchStart}
                    // onTouchMove={handleTouchMove}
                  >
                    <div
                      id="lazy-load-poster"
                      slot="poster"
                      className={classes.webVrPoster}
                    ></div>
                    <button
                      slot="ar-button"
                      className={clsx(classes.webVrButton, "animated-button")}
                    >
                      <img id="ViewInAR" alt="ViewInAR-icon" src={ViewInAR} />
                      <div className={classes.activateARText}>
                        {t("myDesign.webVR.activateAR")}
                      </div>
                    </button>
                    <div id="error" className="hide">
                      {t("myDesign.webVR.ARNotSupported")}
                    </div>
                  </model-viewer>
                  <div className={classes.uploadHdrContainer}>
                    <FormControl className={classes.changeHdrSelect}>
                      <InputLabel htmlFor="HDR-change-select">HDR</InputLabel>
                      <Select
                        value={hdrImage}
                        label={"HDR"}
                        onChange={(e) => {
                          setHdrImage(e.target.value);
                          props.setExparience({
                            ...props.exparience,
                            hdr: e.target.value,
                          });
                        }}
                      >
                        {allHdr.map((item, index) => (
                          <MenuItem
                            key={`skybox-option-${index + 1}`}
                            value={item.url}
                          >
                            {item.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              )}
              <div className={classes.optionsContainer}>
                {props.visibleFrames.length > 0 && selectedFrame && (
                  <FormControl
                    variant="outlined"
                    className={clsx(
                      classes.selectSizeContainer,
                      classes.flexContainer
                    )}
                  >
                    <InputLabel id="selectedFrame-simple-select-outlined-label">
                      {t("myExpariences.selectedFrame")}
                    </InputLabel>
                    <Select
                      labelId="selectedFrame-simple-select-outlined-label"
                      id="selectedFrame-simple-select-outlined"
                      value={selectedFrame}
                      onChange={(e) => setSelectedFrame(e.target.value)}
                      label={t("myExpariences.selectedFrame")}
                      MenuProps={{
                        classes: { paper: classes.selectMenuStyle },
                      }}
                    >
                      {props.visibleFrames.map((frame) => {
                        if (frame.hasOwnProperty("imageUrl") || frame.link)
                          return (
                            <MenuItem value={frame} key={frame.id}>
                              <StyledDiv>
                                <div className={classes.selectContainer}>
                                  {(frame.iconUrl || frame.previewUrl) && (
                                    <img
                                      alt={`visual-frame-${frame.id}`}
                                      className={
                                        classes.imageExparienceDropdown
                                      }
                                      src={frame.iconUrl || frame.previewUrl}
                                    />
                                  )}
                                  {!frame.imageUrl ? (
                                    <Typography
                                      variant="body2"
                                      className={
                                        classes.exparience3DcomponentDropdown
                                      }
                                    >
                                      {
                                        frame.link.split("/")[
                                          frame.link.split("/").length - 1
                                        ]
                                      }
                                    </Typography>
                                  ) : !(frame.iconUrl || frame.previewUrl) ? (
                                    <img
                                      alt={`visual-frame-${frame.id}`}
                                      className={
                                        classes.imageExparienceDropdown
                                      }
                                      src={frame.imageUrl}
                                    />
                                  ) : null}
                                  <span className={classes.selectText}>
                                    {frame.title
                                      ? frame.title
                                      : frame.imageUrl
                                          ?.split("/")
                                          [
                                            frame.imageUrl?.split("/").length -
                                              1
                                          ].split(".")[0] || ""}
                                  </span>
                                </div>
                              </StyledDiv>
                            </MenuItem>
                          );
                        else return null;
                      })}
                    </Select>
                  </FormControl>
                )}
                {props.visibleFrames.length <= 0 && (
                  <Typography
                    variant="body1"
                    className={classes.noFramesVisible}
                  >
                    {t("myDesign.webVR.noFramesVisible")}
                  </Typography>
                )}
                <div className={classes.webArAction}>
                  <TextField
                    name={`web-ar-action`}
                    label={t("myExpariences.form.webArAction")}
                    value={webArActionText}
                    onChange={(e) => {
                      setWebArActionText(e.target.value);
                      props.setExparience({
                        ...props.exparience,
                        webArAction: e.target.value,
                      });
                    }}
                    fullWidth
                    variant={"outlined"}
                  />
                </div>
                <div className={classes.webArAction}>
                  <TextField
                    className={classes.webArActionStyle}
                    label={t("myExpariences.webArActionLink")}
                    id="web-ar-actions"
                    value={webArActionsValue}
                    onChange={(e) => {
                      setWebArActionsValue(e.target.value);
                    }}
                    disabled={
                      webActionLoader ||
                      (webArActionsDisable &&
                        (!selectedFrame?.webArAction ||
                          selectedFrame?.webArAction ===
                            props.exparience.arLink))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {webArActionsDisable &&
                          (!selectedFrame?.webArAction ||
                            selectedFrame?.webArAction ===
                              props.exparience.arLink) ? (
                            <IconButton
                              color="primary"
                              onClick={() => setWebArActionsDisable(false)}
                              size="large"
                            >
                              <EditIcon />
                            </IconButton>
                          ) : (
                            <IconButton
                              color="primary"
                              onClick={() => {
                                setWebActionLoader(true);
                                myExpariencesService
                                  .updateWebArActions(
                                    props.exparience.id,
                                    selectedFrame.id,
                                    webArActionsValue,
                                    props.isAdmin
                                  )
                                  .then((res) => {
                                    const tmpVisible = [
                                      ...props.exparience.objects,
                                    ];
                                    const visibleIndex = tmpVisible.findIndex(
                                      (elem) => elem.id === selectedFrame.id
                                    );
                                    tmpVisible[visibleIndex] = res;
                                    props.setExparience({
                                      ...props.exparience,
                                      objects: tmpVisible,
                                    });
                                    setWebActionLoader(false);
                                    enqueueSnackbar(t("toasts.webArSaved"), {
                                      variant: "success",
                                    });
                                  })
                                  .catch((err) => {
                                    if (err === 403)
                                      enqueueSnackbar(
                                        t("toasts.unauthorizedError"),
                                        {
                                          variant: "error",
                                        }
                                      );
                                    else
                                      enqueueSnackbar(t("toasts.webArFailed"), {
                                        variant: "error",
                                      });
                                  });
                              }}
                              size="large"
                            >
                              {!webActionLoader ? (
                                <SaveIcon />
                              ) : (
                                <CircularProgress size={28} />
                              )}
                            </IconButton>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setUpdateVisibility(true)}
                  className={classes.updateVisiblityButton}
                >
                  {t("myDesign.webVR.updateVisibility")}
                </Button>
                {props.visibleFrames.length > 0 && (
                  <>
                    <TextField
                      name={`contact-email`}
                      className={classes.marginTop}
                      label={t("myExpariences.form.contactEmail")}
                      value={props.exparience.contact}
                      onChange={(e) =>
                        props.setExparience({
                          ...props.exparience,
                          contact: e.target.value,
                        })
                      }
                      error={
                        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                          props.exparience.contact
                        )
                          ? false
                          : true
                      }
                      fullWidth
                      variant={"outlined"}
                    />
                    {!/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
                      props.exparience.contact
                    ) && (
                      <Typography
                        variant="body2"
                        id="email"
                        className={classes.errorEmailStyle}
                      >
                        {t("account.updateEmailForm.wrongEmailFormat")}
                      </Typography>
                    )}
                  </>
                )}
                {props.visibleFrames.length > 0 && (
                  <div>
                    <div className={classes.qrPosition}>
                      {qrUrl && picto && (
                        <CopyToClipboard
                          text={qrUrl}
                          onCopy={() => {
                            setCopied(true);
                            enqueueSnackbar(t("toasts.copiedInClipboard"), {
                              variant: "info",
                            });
                          }}
                        >
                          <div ref={canvasRef}>
                            <QRCode
                              id="react-qrcode-logo"
                              value={qrUrl}
                              size={250}
                              ecLevel="H"
                              bgColor="#000000"
                              fgColor="#D3AF5F" //golden
                              includeMargin={true}
                              qrStyle="dots"
                              logoImage={picto}
                              logoHeight={65}
                              logoWidth={65}
                              eyeRadius={[
                                [0, 100, 100, 100],
                                [100, 0, 100, 100],
                                [100, 100, 100, 0],
                              ]}
                              logoOpacity={1} // the higher the opacity is, the lower the logo has to be
                            />
                          </div>
                        </CopyToClipboard>
                      )}
                    </div>
                    {qrUrl && picto && (
                      <div>
                        <div className={classes.buttonsQrCode}>
                          <Button
                            variant="contained"
                            color="primary"
                            onClick={downloadQR}
                            className={classes.downloadQRButton}
                          >
                            {t("myDesign.webVR.downloadQR")}
                          </Button>
                          <a
                            href={qrUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              className={classes.shareIconButton}
                              size="large"
                            >
                              <LinkIcon />
                            </IconButton>
                          </a>
                          <IconButton
                            className={classes.shareIconButton}
                            onClick={() => shareMedia()}
                            size="large"
                          >
                            <ShareIcon />
                          </IconButton>
                        </div>
                        <div className={classes.copyQrUrlButton}>
                          <CopyToClipboard
                            text={qrUrl}
                            onCopy={() => {
                              setCopied(true);
                              enqueueSnackbar(t("toasts.copiedInClipboard"), {
                                variant: "info",
                              });
                            }}
                          >
                            <Button variant="contained" color="primary">
                              {t("myDesign.webVR.copyShortLink")}
                            </Button>
                          </CopyToClipboard>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </StyledDiv>
      </Container>
    );
  }
};

export default WebVR;
